import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import MarketingLayout from "@/components/marketing/MarketingLayout";
import PricingCard from "@/components/marketing/PricingCard";
import api from "@/lib/api";

/**
 * /pricing/vendors — dedicated Vendor Partner Program page (iter57).
 *
 * Four-tier ladder: Access (Free) → Professional ($1,295) → Enterprise
 * ($2,495) → Featured ($3,595). Professional is the visually highlighted
 * MOST POPULAR tier. Featured Vendor uses a subtle gold accent to signal
 * premium without competing with Professional.
 *
 * Each CTA carries the selected plan through to /vendors/apply as a
 * ?plan= query param so the applicant doesn't have to re-select their
 * tier during onboarding.
 */
export default function VendorPricingPage() {
  const [tiers, setTiers] = useState([]);

  useEffect(() => {
    api.get("/public/pricing/tiers")
      .then(({ data }) => setTiers(data.vendor_tiers || []))
      .catch(() => setTiers([]));
  }, []);

  return (
    <MarketingLayout>
      {/* Page header */}
      <section data-testid="vendor-pricing-header" className="bg-[#F9F8F4]">
        <div className="max-w-4xl mx-auto px-6 sm:px-10 py-24 sm:py-28 text-center">
          <div className="text-[12px] uppercase tracking-[0.32em] font-semibold text-[#B59A5A]">
            Vendor Partner Program
          </div>
          <h1 className="mt-6 font-serif text-4xl sm:text-5xl lg:text-6xl text-[#1A422D] leading-[1.05]">
            Plans designed to grow with your private-club business.
          </h1>
          <p className="mt-8 text-lg text-[#4A4A4A] leading-relaxed max-w-2xl mx-auto">
            Start with complimentary access to the My Crest Club network, then upgrade as your brand needs greater reach, analytics, support, and visibility.
          </p>
        </div>
      </section>

      {/* Pricing cards */}
      <section data-testid="vendor-pricing-cards" className="bg-[#F9F8F4]">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-14 pb-24">
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6 lg:gap-6 items-stretch">
            {tiers.map((tier) => {
              const variant = tier.featured ? "featured" : tier.premium_accent ? "premium" : "default";
              return (
                <PricingCard
                  key={tier.key}
                  tier={tier}
                  variant={variant}
                  ctaTo={`/vendors/apply?plan=${tier.key}`}
                  testId={`vendor-tier-${tier.key}`}
                />
              );
            })}
          </div>

          {/* Progression indicator */}
          <div
            data-testid="vendor-pricing-progression"
            className="mt-16 max-w-4xl mx-auto flex flex-wrap items-center justify-center gap-3 text-[11px] uppercase tracking-[0.28em] font-semibold text-[#B59A5A]"
          >
            <span>Free Access</span>
            <span aria-hidden className="text-[#D9C68A]">→</span>
            <span>Growth</span>
            <span aria-hidden className="text-[#D9C68A]">→</span>
            <span>Scale</span>
            <span aria-hidden className="text-[#D9C68A]">→</span>
            <span>Visibility</span>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section data-testid="vendor-pricing-cta" className="bg-[#F1EFE6] border-y border-[#E5E3D8]">
        <div className="max-w-3xl mx-auto px-6 sm:px-10 py-24 sm:py-28 text-center">
          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-[#1A422D] leading-[1.1]">
            Ready to connect your brand with private-club buyers?
          </h2>
          <p className="mt-6 text-lg text-[#4A4A4A] leading-relaxed">
            Join My Crest Club and give golf shops a better way to discover, request, and work with your brand.
          </p>
          <div className="mt-10 flex flex-wrap justify-center gap-4">
            <Link
              to="/vendors/apply"
              data-testid="vendor-pricing-cta-apply"
              className="inline-flex items-center gap-2 px-9 py-4 bg-[#1A422D] text-[#F9F8F4] text-[13px] uppercase tracking-[0.24em] font-semibold hover:bg-[#0F2A1C] transition-colors"
            >
              Start Your Vendor Application <span aria-hidden>→</span>
            </Link>
          </div>
          <p className="mt-8 text-sm text-[#7A7A7A]">
            Questions? Contact{" "}
            <a href="mailto:theshop@mycrestclub.com" className="text-[#1A422D] font-semibold underline underline-offset-4 hover:text-[#B59A5A]">
              theshop@mycrestclub.com
            </a>
          </p>
        </div>
      </section>
    </MarketingLayout>
  );
}
